// src/pages/Login.js

import React, { useState, useContext } from 'react';
import axiosInstance from '../../utils/axiosConfig'; // Ensure the correct path
import { AuthContext } from '../../authContetxt'; // Corrected typo in 'authContext'
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faKey } from '@fortawesome/free-solid-svg-icons';
import './Login.css'; // Ensure this path is correct
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const { setAuthToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showKillSessions, setShowKillSessions] = useState(false);
  const [isKillingSessions, setIsKillingSessions] = useState(false);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setShowKillSessions(false); // Reset kill sessions button when user changes input
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Function to map English error messages to Turkish
  const mapErrorMessage = (message) => {
    const errorMap = {
      'Invalid username or password': 'Geçersiz kullanıcı adı veya şifre.',
      'User not found': 'Kullanıcı bulunamadı.',
      'Account locked': 'Hesap kilitlendi.',
      // Add more mappings as needed
    };

    return errorMap[message] || 'Giriş sırasında beklenmeyen bir hata oluştu.';
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true); // Start loading
    try {
      const response = await axiosInstance.post('/api/auth/login', formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200 && response.data.token) {
        setAuthToken(response.data.token);
        localStorage.setItem('authToken', response.data.token);
        navigate('/'); // Redirect after successful login
      } else {
        setError('Geçersiz giriş bilgileri.');
      }
    } catch (error) {
      console.error('Giriş hatası:', error.response?.data || error.message);
      const status = error?.response?.status;
      // NEW: explicit handling for single-session policy (already logged in elsewhere)
      if (status === 409) {
        setError('Başka bir cihazda zaten oturum açık. Lütfen önce oradan çıkış yapın.');
        setShowKillSessions(true); // Show kill sessions button
      } else if (error.response && error.response.data && error.response.data.message) {
        const turkishMessage = mapErrorMessage(error.response.data.message);
        setError(turkishMessage);
      } else {
        setError(t('auth.login_error'));
      }
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  // Handle kill all sessions
  const handleKillSessions = async () => {
    if (!formData.username || !formData.password) {
      setError('Lütfen kullanıcı adı ve şifrenizi girin.');
      return;
    }

    setIsKillingSessions(true);
    setError('');
    try {
      const response = await axiosInstance.post('/api/auth/kill-sessions', {
        username: formData.username,
        password: formData.password
      });

      if (response.data.ok) {
        setError('');
        setShowKillSessions(false);
        // Try to login again automatically
        setTimeout(() => {
          const fakeEvent = { preventDefault: () => {} };
          handleSubmit(fakeEvent);
        }, 500);
      } else {
        setError('Oturumları sonlandırma başarısız oldu.');
      }
    } catch (error) {
      console.error('Kill sessions error:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        setError('Geçersiz kullanıcı adı veya şifre.');
      } else {
        setError('Oturumları sonlandırma sırasında bir hata oluştu.');
      }
    } finally {
      setIsKillingSessions(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h4 className="login-title">{t('auth.login')}</h4>
        <p className="login-subtitle">{t('auth.enter_credentials')}</p>

        {error && <div className="login-error">{error}</div>}

        {showKillSessions && (
          <div className="login-kill-sessions" style={{ 
            marginBottom: '15px', 
            padding: '10px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
              Tüm oturumları sonlandırmak istiyor musunuz?
            </p>
            <button
              type="button"
              onClick={handleKillSessions}
              disabled={isKillingSessions || isLoading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isKillingSessions || isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {isKillingSessions ? 'Sonlandırılıyor...' : 'Tüm Oturumları Sonlandır'}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="login-text-area">
            <FontAwesomeIcon icon={faUser} className="login-icon" />
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
              placeholder={t('auth.username')}
              className="login-text-input"
              autoComplete="username"
            />
          </div>

          <div className="login-text-area">
            <FontAwesomeIcon icon={faKey} className="login-icon" />
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder={t('auth.password')}
              className="login-text-input"
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className="login-btn" disabled={isLoading}>
            {isLoading ? t('common.loading') : t('auth.button')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
